import React, { useState, useMemo } from 'react';
import {
  ENS_CONTROLS_CATALOG,
  getEnsControlsByDomain,
  getEnsControlDomains,
  type EnsControl,
  type EnsControlStatus,
} from '../../data/ensControlsCatalog';
import { computeEnsControlsScore } from '../../utils/ensScore';
import { useI18n } from '../../i18n';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buildGovernanceDeepLink } from '../../utils/governanceLinks';
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'

const STATUS_OPTIONS: EnsControlStatus[] = ['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'N_A'];

const STATUS_LABELS: Record<EnsControlStatus, string> = {
  IMPLEMENTED: 'Implemented',
  PARTIAL: 'Partial',
  NOT_IMPLEMENTED: 'Not Implemented',
  N_A: 'N/A',
};

interface EnsControlsPanelProps {
  initialStatuses?: Record<string, EnsControlStatus>;
  onSave?: (statuses: Record<string, EnsControlStatus>) => Promise<void>;
  disabled?: boolean;
}

export default function EnsControlsPanel({
  initialStatuses = {},
  onSave,
  disabled = false,
}: EnsControlsPanelProps) {
  const { t, locale } = useI18n();
  const DOMAIN_ORDER = getEnsControlDomains();
  const [statuses, setStatuses] = useState<Record<string, EnsControlStatus>>(initialStatuses);
  React.useEffect(() => {
    if (initialStatuses && Object.keys(initialStatuses).length > 0) {
      setStatuses((prev) => ({ ...prev, ...initialStatuses }));
    }
  }, [initialStatuses]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set([DOMAIN_ORDER[0]]));
  const [filterDomain, setFilterDomain] = useState<EnsControl['domain'] | 'all'>('all');
  const [isSaving, setIsSaving] = useState(false);

  const handleStatusChange = (controlCode: string, status: EnsControlStatus | '') => {
    if (disabled) return;
    if (status === '') {
      setStatuses((prev) => {
        const next = { ...prev };
        delete next[controlCode];
        return next;
      });
      return;
    }
    setStatuses((prev) => ({ ...prev, [controlCode]: status }));
  };

  const handleSave = async () => {
    if (disabled || isSaving || !onSave) return;
    setIsSaving(true);
    try {
      await onSave(statuses);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDomain = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const controlsByDomain = useMemo(() => {
    if (filterDomain === 'all') {
      return DOMAIN_ORDER.flatMap((d) => getEnsControlsByDomain(d));
    }
    return getEnsControlsByDomain(filterDomain);
  }, [filterDomain, DOMAIN_ORDER]);

  const stats = useMemo(() => {
    const total = ENS_CONTROLS_CATALOG.length;
    const { score, breakdown } = computeEnsControlsScore(statuses);
    return {
      total,
      implemented: breakdown.implemented,
      partial: breakdown.partial,
      notImpl: breakdown.notImplemented,
      na: breakdown.na,
      applicable: breakdown.totalNonNA,
      score,
    };
  }, [statuses]);

  const getTitle = (c: EnsControl) => (locale === 'ru' ? c.title.ru : locale === 'es' ? c.title.es : c.title.en);
  const getDesc = (c: EnsControl) => (locale === 'ru' ? c.description.ru : locale === 'es' ? c.description.es : c.description.en);
  const getRec = (c: EnsControl) => {
    const r = c.recommendations.CRITICAL || c.recommendations.MAJOR || c.recommendations.MODERATE;
    if (!r) return null;
    return locale === 'ru' ? r.ru : locale === 'es' ? r.es : r.en;
  };

  return (
    <div className={`${componentScope('EnsControlsPanel')} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('compliance.ens.controls.title', 'ENS Controls Catalog')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.ens.controls.subtitle', '11 controls • Status: Implemented / Partial / Not Implemented / N/A')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select<EnsControl['domain'] | 'all'>
            options={[
              { value: 'all', label: t('compliance.ens.controls.allDomains', 'All domains') },
              ...DOMAIN_ORDER.map((d) => ({ value: d, label: d })),
            ]}
            value={filterDomain}
            onChange={(v) => v != null && setFilterDomain(v)}
            placeholder={t('compliance.ens.controls.allDomains', 'All domains')}
            minWidth="12rem"
          />
          {onSave && (
            <Button type="button" color="primary" size="small" onClick={handleSave} disabled={disabled || isSaving}>
              {isSaving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex flex-wrap gap-6 text-sm">
          <span>
            <strong>{stats.implemented}</strong> {t('compliance.ens.controls.implemented', 'Implemented')}
          </span>
          <span>
            <strong>{stats.partial}</strong> {t('compliance.ens.controls.partial', 'Partial')}
          </span>
          <span>
            <strong>{stats.notImpl}</strong> {t('compliance.ens.controls.notImplemented', 'Not Implemented')}
          </span>
          <span>
            <strong>{stats.na}</strong> N/A
          </span>
          <span className="ml-auto font-medium">
            {t('compliance.ens.controls.score', 'Controls Score')}: <strong>{stats.score}%</strong>
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {filterDomain === 'all'
          ? DOMAIN_ORDER.map((domain) => {
              const domainControls = getEnsControlsByDomain(domain);
              const isExpanded = expandedDomains.has(domain);
              return (
                <div key={domain} className="rounded-lg border border-gray-200 dark:border-gray-700">
                  <Button
                    type="button"
                    color="outline"
                    onClick={() => toggleDomain(domain)}
                    className="w-full !justify-between rounded-none !border-x-0 !border-t-0 !border-b-gray-200 !bg-transparent !px-4 !py-3 !text-left !font-medium !text-gray-900 !shadow-none dark:!border-b-gray-700 dark:!text-gray-100"
                  >
                    <span className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {domain} ({domainControls.length})
                    </span>
                  </Button>
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {domainControls.map((control) => (
                        <ControlRow
                          key={control.controlCode}
                          control={control}
                          status={statuses[control.controlCode] || null}
                          onStatusChange={handleStatusChange}
                          getTitle={getTitle}
                          getDesc={getDesc}
                          getRec={getRec}
                          disabled={disabled}
                          locale={locale}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          : controlsByDomain.map((control) => (
              <ControlRow
                key={control.controlCode}
                control={control}
                status={statuses[control.controlCode] || null}
                onStatusChange={handleStatusChange}
                getTitle={getTitle}
                getDesc={getDesc}
                getRec={getRec}
                disabled={disabled}
                locale={locale}
              />
            ))}
      </div>
    </div>
  );
}

function ControlRow({
  control,
  status,
  onStatusChange,
  getTitle,
  getDesc,
  getRec,
  disabled,
  locale,
}: {
  control: EnsControl;
  status: EnsControlStatus | null;
  onStatusChange: (code: string, s: EnsControlStatus | '') => void;
  getTitle: (c: EnsControl) => string;
  getDesc: (c: EnsControl) => string;
  getRec: (c: EnsControl) => string | null;
  disabled: boolean;
  locale: string;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const code = control.controlCode.replace('ENS_', '');
  const crossItems = [
    ...(control.crossMapping.ISO27001 || []).map((x) => `ISO27001 ${x}`),
    ...(control.crossMapping.GDPR || []).map((x) => `GDPR ${x}`),
    ...(control.crossMapping.NIS2 || []).map((x) => `NIS2 ${x}`),
  ];
  return (
    <div className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/50">
      <div className="flex flex-wrap items-start gap-4 px-4 py-3">
        <div className="min-w-0 flex-1">
          <Button
            type="button"
            color="outline"
            onClick={() => setShowDetails(!showDetails)}
            className="!border-transparent !bg-transparent !p-0 !text-left !font-medium !text-gray-900 !shadow-none hover:!underline dark:!text-gray-100"
          >
            {code}: {getTitle(control)}
          </Button>
          {control.vendorImpact.applies && (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
              Vendor impact
            </span>
          )}
        </div>
        <Select<string>
          options={[
            { value: '', label: '—' },
            ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
          ]}
          value={status ?? ''}
          onChange={(v) => onStatusChange(control.controlCode, (v ?? '') as EnsControlStatus | '')}
          disabled={disabled}
          minWidth="9rem"
        />
      </div>
      {showDetails && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 text-sm dark:border-gray-700/50 dark:bg-gray-800/30">
          <p className="text-gray-600 dark:text-gray-400">{getDesc(control)}</p>
          {(status === 'PARTIAL' || status === 'NOT_IMPLEMENTED') && getRec(control) && (
            <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 dark:bg-amber-900/20">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-amber-800 dark:text-amber-200">{getRec(control)}</p>
            </div>
          )}
          {crossItems.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Mapped to: {crossItems.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
